import React, { useState, useEffect, useRef, useCallback } from 'react';
import { inject, observer } from 'mobx-react';
import type { UserAction } from '@stores/user/action';
import styles from './index.module.scss';
import { setPostImage } from '@images/imageUrls';
import Avatar from '@components/avatar';

// 防抖动函数
function debounce<T extends (...args: any[]) => void>(func: T, delay: number): T {
    let timeoutId: NodeJS.Timeout;
    return ((...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func(...args), delay);
    }) as T;
}

interface Friend {
    id: string;
    code?: string;
    name: string;
    avatar: string;
    title?: string;
    titleMark?: number;
    isVip?: boolean;
}

interface MentionPickerProps {
    visible: boolean;
    onClose: () => void;
    onSelect: (friends: Friend[]) => boolean | void | Promise<boolean | void>;
    user?: UserAction;
}

const PER_PAGE = 10;

const MentionPickerH5: React.FC<MentionPickerProps> = ({ visible, onClose, onSelect, user }) => {
    const [activeTab, setActiveTab] = useState<'friends' | 'recommend'>('friends');
    const [searchText, setSearchText] = useState('');
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [selectedFriendsMap, setSelectedFriendsMap] = useState<Map<string, Friend>>(new Map());
    const [pageFriends, setPageFriends] = useState(1);
    const [pageRecommend, setPageRecommend] = useState(1);
    const [searchPage, setSearchPage] = useState(1);
    const [isSearching, setIsSearching] = useState(false);
    const pickerRef = useRef<HTMLDivElement>(null);
    const maskRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!visible) {
            setSearchText('');
            setIsSearching(false);
            setSelectedIds(new Set());
            setSelectedFriendsMap(new Map());
            // 重置分页状态
            setPageFriends(1);
            setPageRecommend(1);
            // 离开时清掉搜索结果，避免下次打开混入旧数据
            if (user?.listStore?.clearList) {
                user.listStore.clearList({ namespace: 'searchUsersList' });
            }
            return;
        }

        const handleClickOutside = (e: TouchEvent | MouseEvent) => {
            if (maskRef.current === e.target) {
                onClose();
            }
        };

        document.addEventListener('touchstart', handleClickOutside);
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('touchstart', handleClickOutside);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [visible, onClose, user]);

    const resetSearchState = () => {
        setSearchText('');
        setIsSearching(false);
        if (user?.listStore?.clearList) {
            user.listStore.clearList({ namespace: 'searchUsersList' });
        }
    };

    const loadTabFirstPage = useCallback((tab: 'friends' | 'recommend') => {
        if (!user) return;

        const namespace = tab === 'friends' ? 'followList' : 'recommendUserList';
        if (user.listStore?.clearList) {
            user.listStore.clearList({ namespace });
        }

        if (tab === 'friends') {
            user.fetchFollowList({ page: 1, perPage: PER_PAGE, 'filter[type]': 1 });
            setPageFriends(1);
        } else {
            user.fetchRecommendUserList({ 'page[number]': 1, 'page[size]': PER_PAGE, noRandom: 1 });
            setPageRecommend(1);
        }
    }, [user]);

    // 初次展示加载数据（只在弹窗打开时触发）
    useEffect(() => {
        if (!visible) return;
        loadTabFirstPage(activeTab);
    }, [visible, activeTab, loadTabFirstPage]);

    // 搜索函数
    const performSearch = useCallback(async (keyword: string) => {
        if (!user || !keyword.trim()) {
            setIsSearching(false);
            return;
        }
        
        setIsSearching(true);
        // 每次搜索都清空之前的搜索结果
        if (user.listStore && user.listStore.clearList) {
            user.listStore.clearList({ namespace: 'searchUsersList' });
        }
        
        await user.fetchSearchUsersList({
            page: 1,
            perPage: 20,
            'filter[nickname]': keyword
        });
        setSearchPage(1);
    }, [user]);

    // 防抖动搜索
    const debouncedSearch = useCallback(debounce(performSearch, 500), [performSearch]);

    // 搜索输入变化处理
    const handleSearchChange = (value: string) => {
        setSearchText(value);
        if (value.trim()) {
            debouncedSearch(value.trim());
        } else {
            setIsSearching(false);
            // 清空搜索结果
            if (user?.listStore && user.listStore.clearList) {
                user.listStore.clearList({ namespace: 'searchUsersList' });
            }
        }
    };

    const handleTabChange = (tab: 'friends' | 'recommend') => {
        if (tab === activeTab) return;
        resetSearchState();
        // 先重置分页，清掉旧数据，防止跨 tab 混入
        if (tab === 'friends') {
            setPageFriends(1);
            if (user?.listStore?.clearList) user.listStore.clearList({ namespace: 'followList' });
            user?.fetchFollowList({ page: 1, perPage: PER_PAGE, 'filter[type]': 1 });
        } else {
            setPageRecommend(1);
            if (user?.listStore?.clearList) user.listStore.clearList({ namespace: 'recommendUserList' });
            user?.fetchRecommendUserList({ 'page[number]': 1, 'page[size]': PER_PAGE, noRandom: 1 });
        }
        setActiveTab(tab);
    };

    if (!visible) return null;

    const getList = (ns: string) => user?.listStore.getList({ namespace: ns }) || [];
    const getListState = (ns: string, key: string) => user?.listStore.lists?.[ns]?.[key];

    // 根据是否搜索来决定显示哪个数据源
    const currentRaw = isSearching && searchText.trim() 
        ? getList('searchUsersList')
        : (activeTab === 'friends' ? getList('followList') : getList('recommendUserList'));
    
    const mappedList: Friend[] = currentRaw.map((item: any, idx: number) => {
        // 兼容不同的数据结构，优先从 item.user 中获取，然后再从 item 中获取
        const userInfo = item?.user || item;
        const code = userInfo?.code || userInfo?.uid || userInfo?.userId || userInfo?.id;
        return {
            id: String(userInfo?.userId || userInfo?.code || userInfo?.id || userInfo?.uid || idx),
            code: code ? String(code) : undefined,
            name: userInfo?.nickname || userInfo?.name || userInfo?.username || '',
            avatar: userInfo?.avatar || userInfo?.avatarUrl || userInfo?.headimgurl || '',
            title: userInfo?.title || userInfo?.roleName || item?.group?.groupName || '',
            titleMark: userInfo?.titleMark,
            isVip: !!userInfo?.isVip,
        };
    }).filter(i => i.id && i.name);

    // 搜索时不需要本地过滤，因为已经通过API过滤了
    const filteredList = mappedList;

    const loading = isSearching && searchText.trim()
        ? getListState('searchUsersList', 'loading')
        : getListState(activeTab === 'friends' ? 'followList' : 'recommendUserList', 'loading');
    
    const hasMore = isSearching && searchText.trim()
        ? getListState('searchUsersList', 'hasMore')
        : getListState(activeTab === 'friends' ? 'followList' : 'recommendUserList', 'hasMore');

    const handleToggleSelect = (id: string) => {
        const newSet = new Set(selectedIds);
        const newSelectedFriendsMap = new Map(selectedFriendsMap);
        if (newSet.has(id)) {
            newSet.delete(id);
            newSelectedFriendsMap.delete(id);
        } else {
            newSet.add(id);
            const selectedFriend = mappedList.find(friend => friend.id === id);
            if (selectedFriend) {
                newSelectedFriendsMap.set(id, selectedFriend);
            }
        }
        setSelectedIds(newSet);
        setSelectedFriendsMap(newSelectedFriendsMap);
    };

    const handleConfirm = async () => {
        const selectedFriends = Array.from(selectedFriendsMap.values()).filter(friend => selectedIds.has(friend.id));
        const shouldClose = await onSelect(selectedFriends);
        if (shouldClose === false) {
            return;
        }
        onClose();
    };

    const handleLoadMore = () => {
        if (!user || loading || !hasMore) return;
        
        if (isSearching && searchText.trim()) {
            // 搜索结果的加载更多
            const next = (searchPage || 1) + 1;
            user.fetchSearchUsersList({
                page: next,
                perPage: 20,
                'filter[nickname]': searchText.trim()
            });
            setSearchPage(next);
        } else {
            // 原有的加载更多逻辑
            if (activeTab === 'friends') {
                const next = (pageFriends || 1) + 1;
                user.fetchFollowList({ page: next, perPage: PER_PAGE, 'filter[type]': 1 });
                setPageFriends(next);
            } else {
                const next = (pageRecommend || 1) + 1;
                user.fetchRecommendUserList({ 'page[number]': next, 'page[size]': PER_PAGE, noRandom: 1 });
                setPageRecommend(next);
            }
        }
    };

    const onScrollLoad = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
        if (scrollTop + clientHeight >= scrollHeight - 40) {
            handleLoadMore();
        }
    };

    return (
        <>
            <div className={styles.mask} ref={maskRef}></div>
            <div className={styles.mentionPickerWrap} ref={pickerRef}>
                {/* 标题区域 */}
                <div className={styles.header}>
                    <div className={styles.title}>@好友</div>
                </div>

                {/* 搜索框 */}
                <div className={styles.searchBar}>
                    <div className={styles.searchBox}>
                        <img 
                            src={setPostImage.searchSetpost} 
                            alt="search" 
                            className={styles.searchIcon}
                        />
                        <input
                            type="text"
                            placeholder="搜索好友"
                            className={styles.searchInput}
                            value={searchText}
                            onChange={(e) => handleSearchChange(e.target.value)}
                        />
                    </div>
                </div>

                {/* 切换标签 */}
                <div className={styles.tabBar}>
                    <div
                        className={`${styles.tabItem} ${activeTab === 'friends' ? styles.tabItemActive : ''}`}
                        onClick={() => handleTabChange('friends')}
                    >
                        好友
                        {activeTab === 'friends' && <div className={styles.tabLine}></div>}
                    </div>
                    <div
                        className={`${styles.tabItem} ${activeTab === 'recommend' ? styles.tabItemActive : ''}`}
                        onClick={() => handleTabChange('recommend')}
                    >
                        推荐
                        {activeTab === 'recommend' && <div className={styles.tabLine}></div>}
                    </div>
                </div>

                {/* 好友列表 */}
                <div className={styles.friendList} onScroll={onScrollLoad}>
                    {filteredList.length === 0 && !(isSearching && searchText.trim()) ? (
                        <div className={styles.emptyState}>
                            <img 
                                src={activeTab === 'friends'
                                    ? setPostImage.emptyIconFriends
                                    : setPostImage.emptyIcon}
                                alt="empty" 
                                className={styles.emptyIcon}
                            />
                            <div className={styles.emptyText}>{activeTab === 'friends' ? '还未关注过好友' : '暂无推荐用户'}</div>
                        </div>
                    ) : filteredList.length > 0 ? (
                        filteredList.map((friend) => (
                            <div 
                                key={friend.id} 
                                className={`${styles.friendItem} ${selectedIds.has(friend.id) ? styles.friendItemSelected : ''}`}
                                onClick={() => handleToggleSelect(friend.id)}
                            >
                                <div className={styles.friendInfo}>
                                    <div className={styles.avatarWrapper}>
                                        <Avatar
                                            avatarUrl={friend.avatar}
                                            userCode={friend.code || friend.id}
                                            officialSize={24}
                                            titleMark={friend.titleMark}
                                            size={72}
                                        />
                                        {friend.isVip && (
                                            <img 
                                                src={setPostImage.vIcon} 
                                                alt="vip" 
                                                className={styles.vipIcon}
                                            />
                                        )}
                                    </div>
                                    <div className={styles.friendContent}>
                                        <div className={styles.friendName}>{friend.name}</div>
                                        {friend.title && (
                                            <div className={styles.friendTitle}>{friend.title}</div>
                                        )}
                                    </div>
                                </div>
                                <div className={`${styles.checkIcon} ${selectedIds.has(friend.id) ? styles.checkIconActive : ''}`}>
                                    <img
                                        src={selectedIds.has(friend.id)
                                            ? setPostImage.atCheckoutActive
                                            : setPostImage.atCheckout
                                        }
                                        alt="check"
                                        className={styles.icon}
                                    />
                                </div>
                            </div>
                        ))
                    ) : (isSearching && searchText.trim() && filteredList.length === 0) ? (
                        // 搜索结果为空的提示
                        <div className={styles.emptyState}>
                            <img 
                                src={setPostImage.emptyIcon}
                                alt="empty" 
                                className={styles.emptyIcon}
                            />
                            <div className={styles.emptyText}>没有找到匹配的好友</div>
                        </div>
                    ) : null}
                    {loading && (
                        <div className={styles.loadingMore}>
                            <span className={styles.spinner}></span>
                            <span className={styles.loadingText}>加载中...</span>
                        </div>
                    )}
                    {/* 加载更多按钮 */}
                    {!loading && hasMore && (
                        <div className={styles.loadMore} onClick={handleLoadMore}>加载更多</div>
                    )}
                    {!loading && !hasMore && filteredList.length > 0 && (
                        <div className={styles.noMore}>已经到底啦</div>
                    )}
                </div>

                {/* 底部按钮 */}
                <div className={styles.bottomBar}>
                    <button 
                        className={styles.cancelBtn}
                        onClick={onClose}
                    >
                        取消
                    </button>
                    <button 
                        className={`${styles.confirmBtn} ${selectedIds.size > 0 ? styles.confirmBtnActive : ''}`}
                        onClick={handleConfirm}
                        disabled={selectedIds.size === 0}
                    >
                        确定 {selectedIds.size>0 && <>({selectedIds.size})</>} 
                    </button>
                </div>
            </div>
        </>
    );
};

export default inject('user')(observer(MentionPickerH5));
